import React, { useState, useRef } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { ArrowLeftIcon, SaveIcon, UploadIcon, XIcon, PlusIcon, TrashIcon, UsersIcon, ImageIcon, FileTextIcon, GripVerticalIcon, AwardIcon } from 'lucide-react';
import { AgendaItem } from '../../../types/agenda';
import { ImageCropper } from '../shared/ImageCropper';
interface AgendaSessionEditProps {
  session: AgendaItem;
  onBack: () => void;
  onSave: (updatedSession: AgendaItem) => void;
  onDelete: (sessionId: string) => void;
}
interface Speaker {
  id: string;
  name: string;
}
interface SpeakerGroup {
  id: string;
  title: string;
  speakers: Speaker[];
}
interface LogoItem {
  id: string;
  url: string;
  subtitle: string;
}
// Default speaker group titles
const DEFAULT_GROUP_TITLES = ['Keynote Speakers', 'Panelists', 'Moderators', 'Guest Speakers', 'Workshop Leaders', 'Presenters', 'Facilitators', 'Custom...'];
export function AgendaSessionEdit({
  session,
  onBack,
  onSave,
  onDelete
}: AgendaSessionEditProps) {
  // Basic info state
  const [title, setTitle] = useState(session.title);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date(session.startAt);
    return date.toISOString().split('T')[0];
  });
  const [startTime, setStartTime] = useState(() => {
    const date = new Date(session.startAt);
    return date.toTimeString().slice(0, 5);
  });
  const [endTime, setEndTime] = useState(() => {
    if (session.startAt !== session.endAt) {
      const date = new Date(session.endAt);
      return date.toTimeString().slice(0, 5);
    }
    return '';
  });
  // Rich content state
  const [description, setDescription] = useState(session.description?.type === 'html' ? session.description.value : '');
  const [featureImage, setFeatureImage] = useState(session.image?.url || '');
  // Accreditations and Sponsors state
  const [accreditations, setAccreditations] = useState<LogoItem[]>([]);
  const [sponsors, setSponsors] = useState<LogoItem[]>([]);
  // Image upload and cropping state
  const [cropperFile, setCropperFile] = useState<File | null>(null);
  const [cropperTarget, setCropperTarget] = useState<'feature' | 'accreditation' | 'sponsor' | null>(null);
  const [editingLogoId, setEditingLogoId] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [draggedType, setDraggedType] = useState<'accreditation' | 'sponsor' | null>(null);
  const featureImageInputRef = useRef<HTMLInputElement>(null);
  const accreditationInputRef = useRef<HTMLInputElement>(null);
  const sponsorInputRef = useRef<HTMLInputElement>(null);
  // Speaker groups state
  const [speakerGroups, setSpeakerGroups] = useState<SpeakerGroup[]>(session.speakerGroups?.map(group => ({
    id: group.id,
    title: group.title,
    speakers: group.speakers.map(s => ({
      id: s.id,
      name: s.speaker.id // Using speaker.id as name for now
    }))
  })) || []);
  // UI state
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [showAddSpeakerModal, setShowAddSpeakerModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [selectedGroupTitleOption, setSelectedGroupTitleOption] = useState('');
  const [showCustomGroupTitle, setShowCustomGroupTitle] = useState(false);
  const [newSpeakerName, setNewSpeakerName] = useState('');
  const [showAddAccreditationModal, setShowAddAccreditationModal] = useState(false);
  const [showAddSponsorModal, setShowAddSponsorModal] = useState(false);
  const [newLogoUrl, setNewLogoUrl] = useState('');
  const [newLogoSubtitle, setNewLogoSubtitle] = useState('');
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const [showHtmlSource, setShowHtmlSource] = useState(true); // Default to HTML source
  const handleSave = () => {
    const startDateTime = `${startDate}T${startTime}:00+04:00`;
    const endDateTime = endTime ? `${startDate}T${endTime}:00+04:00` : `${startDate}T${startTime}:00+04:00`;
    const updatedSession: AgendaItem = {
      ...session,
      title,
      startAt: startDateTime,
      endAt: endDateTime,
      description: description ? {
        type: 'html',
        value: description
      } : undefined,
      image: featureImage ? {
        url: featureImage
      } : undefined,
      speakerGroups: speakerGroups.length > 0 ? speakerGroups.map(group => ({
        id: group.id,
        title: group.title,
        speakers: group.speakers.map(s => ({
          id: s.id,
          speaker: {
            id: s.name
          }
        }))
      })) : undefined
    };
    onSave(updatedSession);
    onBack();
  };
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      onDelete(session.id!);
      onBack();
    }
  };
  const handleAddGroup = () => {
    const titleToUse = showCustomGroupTitle ? newGroupTitle : selectedGroupTitleOption;
    if (!titleToUse.trim()) return;
    const newGroup: SpeakerGroup = {
      id: `group-${Date.now()}`,
      title: titleToUse,
      speakers: []
    };
    setSpeakerGroups([...speakerGroups, newGroup]);
    setNewGroupTitle('');
    setSelectedGroupTitleOption('');
    setShowCustomGroupTitle(false);
    setShowAddGroupModal(false);
  };
  const handleGroupTitleChange = (value: string) => {
    setSelectedGroupTitleOption(value);
    if (value === 'Custom...') {
      setShowCustomGroupTitle(true);
      setNewGroupTitle('');
    } else {
      setShowCustomGroupTitle(false);
      setNewGroupTitle(value);
    }
  };
  const handleDeleteGroup = (groupId: string) => {
    setSpeakerGroups(speakerGroups.filter(g => g.id !== groupId));
  };
  const handleAddSpeaker = () => {
    if (!newSpeakerName.trim() || !selectedGroupId) return;
    const newSpeaker: Speaker = {
      id: `speaker-${Date.now()}`,
      name: newSpeakerName
    };
    setSpeakerGroups(speakerGroups.map(group => group.id === selectedGroupId ? {
      ...group,
      speakers: [...group.speakers, newSpeaker]
    } : group));
    setNewSpeakerName('');
    setShowAddSpeakerModal(false);
    setSelectedGroupId(null);
  };
  const handleDeleteSpeaker = (groupId: string, speakerId: string) => {
    setSpeakerGroups(speakerGroups.map(group => group.id === groupId ? {
      ...group,
      speakers: group.speakers.filter(s => s.id !== speakerId)
    } : group));
  };
  const handleAddAccreditation = () => {
    if (!newLogoUrl.trim()) return;
    const newAccreditation: LogoItem = {
      id: `accred-${Date.now()}`,
      url: newLogoUrl,
      subtitle: newLogoSubtitle
    };
    setAccreditations([...accreditations, newAccreditation]);
    setNewLogoUrl('');
    setNewLogoSubtitle('');
    setShowAddAccreditationModal(false);
  };
  const handleDeleteAccreditation = (id: string) => {
    setAccreditations(accreditations.filter(a => a.id !== id));
  };
  const handleAddSponsor = () => {
    if (!newLogoUrl.trim()) return;
    const newSponsor: LogoItem = {
      id: `sponsor-${Date.now()}`,
      url: newLogoUrl,
      subtitle: newLogoSubtitle
    };
    setSponsors([...sponsors, newSponsor]);
    setNewLogoUrl('');
    setNewLogoSubtitle('');
    setShowAddSponsorModal(false);
  };
  const handleDeleteSponsor = (id: string) => {
    setSponsors(sponsors.filter(s => s.id !== id));
  };
  // Image upload handlers
  const handleFeatureImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCropperFile(file);
      setCropperTarget('feature');
    }
  };
  const handleAccreditationSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCropperFile(file);
      setCropperTarget('accreditation');
    }
  };
  const handleSponsorSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCropperFile(file);
      setCropperTarget('sponsor');
    }
  };
  const handleCropComplete = (croppedImageUrl: string) => {
    if (cropperTarget === 'feature') {
      setFeatureImage(croppedImageUrl);
    } else if (cropperTarget === 'accreditation') {
      const newAccreditation: LogoItem = {
        id: `accred-${Date.now()}`,
        url: croppedImageUrl,
        subtitle: ''
      };
      setAccreditations([...accreditations, newAccreditation]);
    } else if (cropperTarget === 'sponsor') {
      const newSponsor: LogoItem = {
        id: `sponsor-${Date.now()}`,
        url: croppedImageUrl,
        subtitle: ''
      };
      setSponsors([...sponsors, newSponsor]);
    }
    setCropperFile(null);
    setCropperTarget(null);
  };
  const handleCropCancel = () => {
    setCropperFile(null);
    setCropperTarget(null);
  };
  const handleUpdateSubtitle = (id: string, subtitle: string, type: 'accreditation' | 'sponsor') => {
    if (type === 'accreditation') {
      setAccreditations(accreditations.map(a => a.id === id ? {
        ...a,
        subtitle
      } : a));
    } else {
      setSponsors(sponsors.map(s => s.id === id ? {
        ...s,
        subtitle
      } : s));
    }
    setEditingLogoId(null);
  };
  // Drag and drop handlers
  const handleDragStart = (index: number, type: 'accreditation' | 'sponsor') => {
    setDraggedIndex(index);
    setDraggedType(type);
  };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };
  const handleDrop = (e: React.DragEvent, dropIndex: number, type: 'accreditation' | 'sponsor') => {
    e.preventDefault();
    if (draggedIndex === null || draggedType !== type) return;
    if (type === 'accreditation') {
      const newAccreditations = [...accreditations];
      const [removed] = newAccreditations.splice(draggedIndex, 1);
      newAccreditations.splice(dropIndex, 0, removed);
      setAccreditations(newAccreditations);
    } else {
      const newSponsors = [...sponsors];
      const [removed] = newSponsors.splice(draggedIndex, 1);
      newSponsors.splice(dropIndex, 0, removed);
      setSponsors(newSponsors);
    }
    setDraggedIndex(null);
    setDraggedType(null);
  };
  // Rich text editor functions
  const insertFormatting = (before: string, after: string = '') => {
    const textarea = descriptionRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = description.substring(start, end);
    const newText = description.substring(0, start) + before + selectedText + after + description.substring(end);
    setDescription(newText);
    // Set cursor position after insertion
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + before.length + selectedText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };
  const insertBold = () => insertFormatting('<strong>', '</strong>');
  const insertItalic = () => insertFormatting('<em>', '</em>');
  const insertHeading = () => insertFormatting('<h3>', '</h3>');
  const insertList = () => insertFormatting('<ul>\n  <li>', '</li>\n</ul>');
  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      insertFormatting(`<a href="${url}">`, '</a>');
    }
  };
  const formatTime = (isoDateTime: string) => {
    const date = new Date(isoDateTime);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  const formatDate = (isoDateTime: string) => {
    const date = new Date(isoDateTime);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  return <div className="min-h-screen bg-gray-50 -m-8" data-id="element-659">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-10" data-id="element-660">
        <div className="px-8 py-4" data-id="element-661">
          <div className="flex items-center justify-between" data-id="element-662">
            <div className="flex items-center gap-4" data-id="element-663">
              <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-664">
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-665" />
              </button>
              <div data-id="element-666">
                <h1 className="text-xl font-bold text-gray-900" data-id="element-667">
                  Edit Session
                </h1>
                <p className="text-sm text-gray-500 mt-0.5" data-id="element-668">
                  {formatDate(session.startAt)} • {formatTime(session.startAt)}
                  {session.startAt !== session.endAt && ` - ${formatTime(session.endAt)}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-669">
              <Button variant="outline" onClick={handleDelete} className="text-red-600 hover:bg-red-50" data-id="element-670">
                <TrashIcon className="w-4 h-4 mr-2" data-id="element-671" />
                Delete Session
              </Button>
              <Button variant="primary" onClick={handleSave} data-id="element-672">
                <SaveIcon className="w-4 h-4 mr-2" data-id="element-673" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 py-8 max-w-5xl mx-auto space-y-6" data-id="element-674">
        {/* Basic Information */}
        <Card padding="lg" data-id="element-675">
          <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-676">
            Basic Information
          </h2>
          <div className="space-y-6" data-id="element-677">
            <div data-id="element-678">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-679">
                Session Title *
              </label>
              <textarea value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Opening Keynote: The Future of Fitness Technology" className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-h-[80px] resize-none" data-id="element-680" />
            </div>

            <div className="grid grid-cols-3 gap-4" data-id="element-681">
              <div data-id="element-682">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-683">
                  Date *
                </label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-id="element-684" />
              </div>
              <div data-id="element-685">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-686">
                  Start Time *
                </label>
                <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} data-id="element-687" />
              </div>
              <div data-id="element-688">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-689">
                  End Time
                </label>
                <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} placeholder="Optional" data-id="element-690" />
              </div>
            </div>
          </div>
        </Card>

        {/* Description */}
        <Card padding="lg" data-id="element-691">
          <div className="flex items-center justify-between mb-6" data-id="element-692">
            <div className="flex items-center gap-2" data-id="element-693">
              <FileTextIcon className="w-5 h-5 text-gray-700" data-id="element-694" />
              <h2 className="text-lg font-bold text-gray-900" data-id="element-695">Description</h2>
            </div>
          </div>
          <div data-id="element-696">
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Add a detailed description of this session..." className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-h-[200px]" data-id="element-697" />
          </div>
        </Card>

        {/* Feature Image */}
        <Card padding="lg" data-id="element-698">
          <div className="flex items-center justify-between mb-6" data-id="element-699">
            <div className="flex items-center gap-2" data-id="element-700">
              <ImageIcon className="w-5 h-5 text-gray-700" data-id="element-701" />
              <h2 className="text-lg font-bold text-gray-900" data-id="element-702">Feature Image</h2>
            </div>
          </div>
          <div className="space-y-4" data-id="element-703">
            {featureImage ? <div className="relative w-full max-w-md" data-id="element-704">
                <div className="aspect-square w-full" data-id="element-705">
                  <img src={featureImage} alt="Feature" className="w-full h-full object-cover rounded-lg border border-gray-200" data-id="element-706" />
                </div>
                <button onClick={() => setFeatureImage('')} className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors" data-id="element-707">
                  <XIcon className="w-4 h-4 text-gray-600" data-id="element-708" />
                </button>
              </div> : <button onClick={() => featureImageInputRef.current?.click()} className="w-full max-w-md aspect-square border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors flex flex-col items-center justify-center gap-3 bg-gray-50 hover:bg-gray-100" data-id="element-709">
                <UploadIcon className="w-12 h-12 text-gray-400" data-id="element-710" />
                <div className="text-center" data-id="element-711">
                  <p className="text-sm font-medium text-gray-700" data-id="element-712">
                    Click to upload feature image
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-713">
                    JPG or PNG • Will be cropped to square
                  </p>
                </div>
              </button>}
            <input ref={featureImageInputRef} type="file" accept="image/jpeg,image/png" onChange={handleFeatureImageSelect} className="hidden" data-id="element-714" />
          </div>
        </Card>

        {/* Accreditations */}
        <Card padding="lg" data-id="element-715">
          <div className="flex items-center justify-between mb-6" data-id="element-716">
            <div className="flex items-center gap-2" data-id="element-717">
              <AwardIcon className="w-5 h-5 text-gray-700" data-id="element-718" />
              <h2 className="text-lg font-bold text-gray-900" data-id="element-719">
                Accreditations
              </h2>
            </div>
            <Button variant="outline" size="sm" onClick={() => accreditationInputRef.current?.click()} data-id="element-720">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-721" />
              Add Logo
            </Button>
          </div>

          {accreditations.length === 0 ? <button onClick={() => accreditationInputRef.current?.click()} className="w-full text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-100 transition-colors" data-id="element-722">
              <AwardIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" data-id="element-723" />
              <p className="text-sm text-gray-600 mb-1" data-id="element-724">
                No accreditation logos yet
              </p>
              <p className="text-xs text-gray-500" data-id="element-725">
                Click to upload your first logo
              </p>
            </button> : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-726">
              {accreditations.map((accred, index) => <div key={accred.id} draggable onDragStart={() => handleDragStart(index, 'accreditation')} onDragOver={e => handleDragOver(e, index)} onDrop={e => handleDrop(e, index, 'accreditation')} className="relative group cursor-move" data-id="element-727">
                  <div className="border border-gray-200 rounded-lg p-4 bg-white hover:border-gray-300 transition-colors" data-id="element-728">
                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-729">
                      <GripVerticalIcon className="w-4 h-4 text-gray-400" data-id="element-730" />
                    </div>
                    <div className="aspect-square mb-2 flex items-center justify-center bg-gray-50 rounded" data-id="element-731">
                      <img src={accred.url} alt={accred.subtitle} className="max-w-full max-h-full object-contain" data-id="element-732" />
                    </div>
                    <input type="text" value={accred.subtitle} onChange={e => handleUpdateSubtitle(accred.id, e.target.value, 'accreditation')} placeholder="Add subtitle..." className="w-full text-xs text-center border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-733" />
                  </div>
                  <button onClick={() => handleDeleteAccreditation(accred.id)} className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100" data-id="element-734">
                    <XIcon className="w-3 h-3 text-red-600" data-id="element-735" />
                  </button>
                </div>)}
            </div>}
          <input ref={accreditationInputRef} type="file" accept="image/jpeg,image/png" onChange={handleAccreditationSelect} className="hidden" data-id="element-736" />
        </Card>

        {/* Sponsors */}
        <Card padding="lg" data-id="element-737">
          <div className="flex items-center justify-between mb-6" data-id="element-738">
            <div className="flex items-center gap-2" data-id="element-739">
              <ImageIcon className="w-5 h-5 text-gray-700" data-id="element-740" />
              <h2 className="text-lg font-bold text-gray-900" data-id="element-741">Sponsors</h2>
            </div>
            <Button variant="outline" size="sm" onClick={() => sponsorInputRef.current?.click()} data-id="element-742">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-743" />
              Add Logo
            </Button>
          </div>

          {sponsors.length === 0 ? <button onClick={() => sponsorInputRef.current?.click()} className="w-full text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-100 transition-colors" data-id="element-744">
              <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" data-id="element-745" />
              <p className="text-sm text-gray-600 mb-1" data-id="element-746">No sponsor logos yet</p>
              <p className="text-xs text-gray-500" data-id="element-747">
                Click to upload your first logo
              </p>
            </button> : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-748">
              {sponsors.map((sponsor, index) => <div key={sponsor.id} draggable onDragStart={() => handleDragStart(index, 'sponsor')} onDragOver={e => handleDragOver(e, index)} onDrop={e => handleDrop(e, index, 'sponsor')} className="relative group cursor-move" data-id="element-749">
                  <div className="border border-gray-200 rounded-lg p-4 bg-white hover:border-gray-300 transition-colors" data-id="element-750">
                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-751">
                      <GripVerticalIcon className="w-4 h-4 text-gray-400" data-id="element-752" />
                    </div>
                    <div className="aspect-square mb-2 flex items-center justify-center bg-gray-50 rounded" data-id="element-753">
                      <img src={sponsor.url} alt={sponsor.subtitle} className="max-w-full max-h-full object-contain" data-id="element-754" />
                    </div>
                    <input type="text" value={sponsor.subtitle} onChange={e => handleUpdateSubtitle(sponsor.id, e.target.value, 'sponsor')} placeholder="Add subtitle..." className="w-full text-xs text-center border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-755" />
                  </div>
                  <button onClick={() => handleDeleteSponsor(sponsor.id)} className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100" data-id="element-756">
                    <XIcon className="w-3 h-3 text-red-600" data-id="element-757" />
                  </button>
                </div>)}
            </div>}
          <input ref={sponsorInputRef} type="file" accept="image/jpeg,image/png" onChange={handleSponsorSelect} className="hidden" data-id="element-758" />
        </Card>

        {/* Speaker Groups */}
        <Card padding="lg" data-id="element-759">
          <div className="flex items-center justify-between mb-6" data-id="element-760">
            <div className="flex items-center gap-2" data-id="element-761">
              <UsersIcon className="w-5 h-5 text-gray-700" data-id="element-762" />
              <h2 className="text-lg font-bold text-gray-900" data-id="element-763">
                Speaker Groups
              </h2>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowAddGroupModal(true)} data-id="element-764">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-765" />
              Add Group
            </Button>
          </div>

          {speakerGroups.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-766">
              <UsersIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" data-id="element-767" />
              <p className="text-sm text-gray-600 mb-3" data-id="element-768">
                No speaker groups yet
              </p>
              <Button variant="outline" size="sm" onClick={() => setShowAddGroupModal(true)} data-id="element-769">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-770" />
                Add First Group
              </Button>
            </div> : <div className="space-y-4" data-id="element-771">
              {speakerGroups.map(group => <div key={group.id} className="border border-gray-200 rounded-lg p-4" data-id="element-772">
                  <div className="flex items-center justify-between mb-3" data-id="element-773">
                    <h3 className="font-semibold text-gray-900" data-id="element-774">
                      {group.title}
                    </h3>
                    <div className="flex items-center gap-2" data-id="element-775">
                      <Button variant="outline" size="sm" onClick={() => {
                  setSelectedGroupId(group.id);
                  setShowAddSpeakerModal(true);
                }} data-id="element-776">
                        <PlusIcon className="w-3 h-3 mr-1" data-id="element-777" />
                        Add Speaker
                      </Button>
                      <button onClick={() => handleDeleteGroup(group.id)} className="text-gray-400 hover:text-red-600 p-1" data-id="element-778">
                        <TrashIcon className="w-4 h-4" data-id="element-779" />
                      </button>
                    </div>
                  </div>

                  {group.speakers.length === 0 ? <p className="text-sm text-gray-500 italic" data-id="element-780">
                      No speakers in this group
                    </p> : <div className="space-y-2" data-id="element-781">
                      {group.speakers.map(speaker => <div key={speaker.id} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-id="element-782">
                          <span className="text-sm text-gray-900" data-id="element-783">
                            {speaker.name}
                          </span>
                          <button onClick={() => handleDeleteSpeaker(group.id, speaker.id)} className="text-gray-400 hover:text-red-600" data-id="element-784">
                            <XIcon className="w-4 h-4" data-id="element-785" />
                          </button>
                        </div>)}
                    </div>}
                </div>)}
            </div>}
        </Card>
      </div>

      {/* Image Cropper Modal */}
      {cropperFile && <ImageCropper imageFile={cropperFile} onCropComplete={handleCropComplete} onCancel={handleCropCancel} data-id="element-786" />}

      {/* Add Group Modal */}
      {showAddGroupModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-787">
          <Card className="w-full max-w-md" data-id="element-788">
            <div className="p-6" data-id="element-789">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-790">
                Add Speaker Group
              </h3>
              <div className="space-y-4" data-id="element-791">
                <div data-id="element-792">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-793">
                    Group Title *
                  </label>
                  <select value={selectedGroupTitleOption} onChange={e => handleGroupTitleChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-794">
                    <option value="" data-id="element-795">Select a group type...</option>
                    {DEFAULT_GROUP_TITLES.map(title => <option key={title} value={title} data-id="element-796">
                        {title}
                      </option>)}
                  </select>
                </div>

                {showCustomGroupTitle && <div data-id="element-797">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-798">
                      Custom Group Title *
                    </label>
                    <Input type="text" value={newGroupTitle} onChange={e => setNewGroupTitle(e.target.value)} placeholder="e.g., Industry Experts, Special Guests" autoFocus data-id="element-799" />
                  </div>}
              </div>
              <div className="flex gap-3 mt-6" data-id="element-800">
                <Button variant="outline" onClick={() => {
              setShowAddGroupModal(false);
              setNewGroupTitle('');
              setSelectedGroupTitleOption('');
              setShowCustomGroupTitle(false);
            }} className="flex-1" data-id="element-801">
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAddGroup} disabled={showCustomGroupTitle ? !newGroupTitle.trim() : !selectedGroupTitleOption || selectedGroupTitleOption === 'Custom...'} className="flex-1" data-id="element-802">
                  Add Group
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* Add Speaker Modal */}
      {showAddSpeakerModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-803">
          <Card className="w-full max-w-md" data-id="element-804">
            <div className="p-6" data-id="element-805">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-806">
                Add Speaker
              </h3>
              <div className="space-y-4" data-id="element-807">
                <div data-id="element-808">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-809">
                    Speaker Name *
                  </label>
                  <Input type="text" value={newSpeakerName} onChange={e => setNewSpeakerName(e.target.value)} placeholder="e.g., Dr. Sarah Johnson" data-id="element-810" />
                </div>
              </div>
              <div className="flex gap-3 mt-6" data-id="element-811">
                <Button variant="outline" onClick={() => {
              setShowAddSpeakerModal(false);
              setNewSpeakerName('');
              setSelectedGroupId(null);
            }} className="flex-1" data-id="element-812">
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAddSpeaker} disabled={!newSpeakerName.trim()} className="flex-1" data-id="element-813">
                  Add Speaker
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* Add Accreditation Modal */}
      {showAddAccreditationModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-814">
          <Card className="w-full max-w-md" data-id="element-815">
            <div className="p-6" data-id="element-816">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-817">
                Add Accreditation Logo
              </h3>
              <div className="space-y-4" data-id="element-818">
                <div data-id="element-819">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-820">
                    Logo URL *
                  </label>
                  <Input type="url" value={newLogoUrl} onChange={e => setNewLogoUrl(e.target.value)} placeholder="https://example.com/logo.png" data-id="element-821" />
                </div>
                <div data-id="element-822">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-823">
                    Subtitle
                  </label>
                  <Input type="text" value={newLogoSubtitle} onChange={e => setNewLogoSubtitle(e.target.value)} placeholder="e.g., CPD Accredited, ISO Certified" data-id="element-824" />
                </div>
              </div>
              <div className="flex gap-3 mt-6" data-id="element-825">
                <Button variant="outline" onClick={() => {
              setShowAddAccreditationModal(false);
              setNewLogoUrl('');
              setNewLogoSubtitle('');
            }} className="flex-1" data-id="element-826">
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAddAccreditation} disabled={!newLogoUrl.trim()} className="flex-1" data-id="element-827">
                  Add Logo
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* Add Sponsor Modal */}
      {showAddSponsorModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-828">
          <Card className="w-full max-w-md" data-id="element-829">
            <div className="p-6" data-id="element-830">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-831">
                Add Sponsor Logo
              </h3>
              <div className="space-y-4" data-id="element-832">
                <div data-id="element-833">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-834">
                    Logo URL *
                  </label>
                  <Input type="url" value={newLogoUrl} onChange={e => setNewLogoUrl(e.target.value)} placeholder="https://example.com/logo.png" data-id="element-835" />
                </div>
                <div data-id="element-836">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-837">
                    Subtitle
                  </label>
                  <Input type="text" value={newLogoSubtitle} onChange={e => setNewLogoSubtitle(e.target.value)} placeholder="e.g., Platinum Sponsor, Event Partner" data-id="element-838" />
                </div>
              </div>
              <div className="flex gap-3 mt-6" data-id="element-839">
                <Button variant="outline" onClick={() => {
              setShowAddSponsorModal(false);
              setNewLogoUrl('');
              setNewLogoSubtitle('');
            }} className="flex-1" data-id="element-840">
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAddSponsor} disabled={!newLogoUrl.trim()} className="flex-1" data-id="element-841">
                  Add Logo
                </Button>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}